import { useNavigate } from "@tanstack/react-router";
import { LayoutTemplate, Library, Mail, Mic, Plus } from "lucide-react";
import { motion } from "motion/react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import type { Template } from "@/common/types/templates";
import { PillHashTabs } from "@/components/ui/redesign/hash-tabs";
import {
  HeaderActionLabel,
  PAGE_COLUMN,
  PageHeader,
  TOUCH_BUTTON,
} from "@/components/ui/redesign/page";
import { ReferralTemplatesSection } from "@/components/ui/referral/ReferralTemplatesSection";
import { SubscriptionOnlyButton } from "@/components/ui/SubscriptionOnlyButton/SubscriptionOnlyButton";
import { SpokenShortcuts } from "@/components/ui/templates/spoken-shortcuts";
import { cn, container } from "@/lib/utils";
import { MyTemplates } from "./MyTemplates";
import { TemplateLibrary } from "./TemplateLibrary";

interface Props {
  templates: Template[];
  loading: boolean;
  error: boolean;
  ready: boolean;
  onRetry: () => void;
}

// "New Template": in the top bar it reads "New" on phones.
export const NewTemplateButton = ({
  className,
  compact = false,
}: {
  className?: string;
  compact?: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <SubscriptionOnlyButton
      className={cn(
        TOUCH_BUTTON,
        "gap-1.5 px-4",
        compact && "max-sm:px-3",
        className,
      )}
      onClick={async () => {
        await navigate({ to: "/template/new" });
      }}
    >
      <Plus aria-hidden />
      {compact ? (
        <HeaderActionLabel
          full={t("templates.newTemplateButton")}
          short={t("templates.home.newShort")}
        />
      ) : (
        t("templates.newTemplateButton")
      )}
    </SubscriptionOnlyButton>
  );
};

// Templates home (title, count and New Template are in the top bar), then
// My templates, Library and
// Spoken shortcuts as pill tabs (kept in the URL hash as before).
export const TemplatesHome = ({
  templates,
  loading,
  error,
  ready,
  onRetry,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <>
      <PageHeader title={t("templates.title")} />
      <motion.div
        animate="show"
        className={cn(PAGE_COLUMN, "space-y-5 sm:space-y-6")}
        data-testid="templates-home"
        initial="hidden"
        variants={container}
      >
        <PillHashTabs
          label={t("templates.home.tabsLabel")}
          v3={AppConfig.enableReferralTemplates}
          tabs={[
            {
              value: "my-templates",
              label: t("templates.myTemplatesTab"),
              icon: <LayoutTemplate aria-hidden />,
              content: (
                <MyTemplates
                  error={error}
                  loading={loading}
                  newTemplateButton={
                    <NewTemplateButton className="max-sm:w-full" />
                  }
                  ready={ready}
                  templates={templates}
                  onBrowseLibrary={() => {
                    void navigate({ hash: "library", replace: true } as never);
                  }}
                  onRetry={onRetry}
                />
              ),
            },
            {
              value: "library",
              label: t("templates.libraryTab"),
              icon: <Library aria-hidden />,
              content: <TemplateLibrary />,
            },
            ...(AppConfig.enableReferralTemplates
              ? [
                  {
                    value: "referral-letters",
                    label: t("referral.templates.tab"),
                    icon: <Mail aria-hidden />,
                    content: <ReferralTemplatesSection />,
                  },
                ]
              : []),
            {
              value: "shortcuts",
              label: t("shortcuts.tab"),
              icon: <Mic aria-hidden />,
              // Its own v3.2 panels: the form, then the list.
              content: (
                <div data-testid="shortcuts-card">
                  <SpokenShortcuts />
                </div>
              ),
            },
          ]}
        />
      </motion.div>
    </>
  );
};
